import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface DashboardFABProps {
  /** Called when the FAB is clicked — default: navigate to /dashboard */
  onClick?: () => void;
  /** Label shown on hover/focus and in aria-label */
  label?: string;
  /** Icon slot — defaults to a ▶ play icon */
  icon?: React.ReactNode;
}

/**
 * DashboardFAB
 *
 * A Floating Action Button shown in the bottom-right corner of the dashboard
 * on mobile (< 1024px) only. Nudges users to start their next simulation.
 *
 * Hidden on desktop — desktop has the full header nav and inline CTAs.
 * Hidden during scroll-down to keep content visible; reappears on scroll-up.
 */
export const DashboardFAB: React.FC<DashboardFABProps> = ({
  onClick,
  label = 'Start Simulation',
  icon,
}) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Hide FAB while scrolling down, show while scrolling up
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setVisible(currentY < lastScrollY || currentY < 60);
      setLastScrollY(currentY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <button
      type="button"
      className={`dashboard-fab${visible ? ' visible' : ''}`}
      onClick={handleClick}
      aria-label={label}
      title={label}
    >
      <span className="dashboard-fab__icon">
        {icon || (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" stroke="none" />
          </svg>
        )}
      </span>
      <span className="dashboard-fab__label">{label}</span>
    </button>
  );
};
